import { PlusIcon, PencilSquareIcon, TrashIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { Button } from '../../Button/Button';
import { useWithholdingsTranslation } from './i18n';
import type { WithholdingDto } from './types';

export interface WithholdingsActionBarProps {
  /** Permiso de escritura (Agregar/Modificar/Eliminar). */
  canWrite: boolean;
  /** Permiso de recálculo (Recalcular). */
  canRecalculate: boolean;
  /** `true` si hay loaders de catálogo (habilita Agregar). */
  hasCatalogs: boolean;
  /** Fila seleccionada (habilita Modificar/Eliminar). */
  selectedRow: WithholdingDto | null;
  /** Alguna mutación en vuelo: deshabilita la barra. */
  isBusy: boolean;
  onAdd: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onRecalculate: () => void;
}

/**
 * Barra de acciones del panel de retenciones (FEAT-105-11.02).
 *
 * El padre solo monta esta barra cuando el documento es editable y el usuario
 * tiene algún permiso (gating de estado/permiso → la barra se oculta, no se
 * deshabilita). Aquí se gatea por permiso fino y por selección:
 * - Agregar: requiere `canWrite` + catálogos.
 * - Modificar: requiere `canWrite` + fila seleccionada.
 * - Eliminar: requiere `canWrite` + fila seleccionada **manual** (las
 *   automáticas/manipuladas devuelven 409).
 * - Recalcular: requiere `canRecalculate`.
 */
export function WithholdingsActionBar({
  canWrite,
  canRecalculate,
  hasCatalogs,
  selectedRow,
  isBusy,
  onAdd,
  onEdit,
  onDelete,
  onRecalculate,
}: WithholdingsActionBarProps) {
  const { t } = useWithholdingsTranslation();

  const hasSelection = Boolean(selectedRow);
  const canDeleteSelected = hasSelection && Boolean(selectedRow?.isManual);

  return (
    <div className="flex flex-wrap items-center gap-2">
      {canWrite && hasCatalogs && (
        <Button
          type="default"
          size="sm"
          leftIcon={<PlusIcon className="w-4 h-4" />}
          disabled={isBusy}
          onClick={onAdd}
        >
          {t('withholdings.actions.add')}
        </Button>
      )}

      {canWrite && (
        <Button
          type="outline"
          size="sm"
          leftIcon={<PencilSquareIcon className="w-4 h-4" />}
          disabled={isBusy || !hasSelection}
          onClick={onEdit}
        >
          {t('withholdings.actions.edit')}
        </Button>
      )}

      {canWrite && (
        <Button
          type="outline"
          size="sm"
          leftIcon={<TrashIcon className="w-4 h-4" />}
          disabled={isBusy || !canDeleteSelected}
          onClick={onDelete}
        >
          {t('withholdings.actions.delete')}
        </Button>
      )}

      {canRecalculate && (
        <Button
          type="outline"
          size="sm"
          leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          disabled={isBusy}
          onClick={onRecalculate}
          className="ml-auto"
        >
          {t('withholdings.actions.recalculate')}
        </Button>
      )}
    </div>
  );
}
